import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerPosition,
} from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import 'air-datepicker/air-datepicker.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactTabulator, type ReactTabulatorOptions } from 'react-tabulator';
import type { CellComponent, ColumnDefinition } from 'tabulator-tables';

import useDirectionalTraffic from '@/domains/statistic/hooks/useDirectionalTraffic';
import type {
  DirectionalTrafficRecord,
  RoadColumn,
} from '@/domains/statistic/types/directional-traffic.types';
import Button from '@/shared/components/ui/Button/Button';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import Select from '@/shared/components/ui/Select/Select';
import useSiteManagement from '@/shared/hooks/useSiteManagement';
import { formatDateTime, getLocalISOString } from '@/shared/utils/date';

const DirectionalTrafficStatisticPage = () => {
  const { i18n } = useTranslation();

  const midnight = new Date(new Date().setHours(0, 0, 0, 0));
  const today = new Date();

  // Pagination constants
  const PAGE_SIZE = 10;

  // State
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(undefined);
  const [activeButton, setActiveButton] = useState<string>('1시간');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [interval, setInterval] = useState<string>('1h');
  const [dateTime, setDateTime] = useState<{ start_date: string; end_date: string }>({
    start_date: formatDateTime(midnight),
    end_date: formatDateTime(today),
  });
  // Store original date range for toggle reset
  const [_originalDateTime] = useState({
    start_date: formatDateTime(midnight),
    end_date: formatDateTime(today),
  });

  // Initialize query params - empty until site is selected
  const [queryParams, setQueryParams] = useState<string>('');

  // Refs to access latest values in datepicker callbacks
  const selectedSiteIdRef = useRef<number | undefined>(selectedSiteId);
  const dateTimeRef = useRef(dateTime);
  const intervalRef = useRef(interval);

  // Keep refs in sync with state
  useEffect(() => {
    selectedSiteIdRef.current = selectedSiteId;
  }, [selectedSiteId]);

  useEffect(() => {
    dateTimeRef.current = dateTime;
  }, [dateTime]);

  useEffect(() => {
    intervalRef.current = interval;
  }, [interval]);

  // Fetch site list
  const { siteListData } = useSiteManagement({ queryParams: 'page=1&size=10000', siteId: null });

  // Fetch directional traffic data - only when queryParams has site_id
  const { directionalTrafficData, isLoading } = useDirectionalTraffic({
    queryParams,
    enabled: !!queryParams && queryParams.includes('site_id'),
  });

  // Site options for select box (no placeholder, first site is default)
  const siteOptions = useMemo(() => {
    if (!siteListData?.data || siteListData.data.length === 0) return [];
    return siteListData.data.map((site) => ({
      label: site.name,
      value: String(site.site_id),
    }));
  }, [siteListData]);

  // Auto-select first site and trigger initial query when site list loads
  useEffect(() => {
    if (siteListData?.data && siteListData.data.length > 0 && !selectedSiteId) {
      const firstSite = siteListData.data[0];
      if (!firstSite) return;
      const firstSiteId = firstSite.site_id;
      setSelectedSiteId(firstSiteId);

      // Trigger initial query with default date range and pagination
      const params = new URLSearchParams();
      params.append('site_id', firstSiteId.toString());
      params.append('start_time', getLocalISOString(dateTime.start_date || ''));
      params.append('end_time', getLocalISOString(dateTime.end_date || ''));
      params.append('page', '1');
      params.append('size', PAGE_SIZE.toString());
      params.append('sort', 'desc');
      params.append('interval', '1h');
      setQueryParams(params.toString());
    }
  }, [siteListData, selectedSiteId, dateTime.start_date, dateTime.end_date]);

  // Handle input change for date inputs
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateTime((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  // Helper function to trigger API call
  const triggerQuery = useCallback(
    (
      siteId: number,
      startDate: string,
      endDate: string,
      page: number = 1,
      intervalParam: string = '1h'
    ) => {
      const params = new URLSearchParams();
      params.append('site_id', siteId.toString());
      params.append('start_time', getLocalISOString(startDate));
      params.append('end_time', getLocalISOString(endDate));
      params.append('page', page.toString());
      params.append('size', PAGE_SIZE.toString());
      params.append('sort', 'desc');
      params.append('interval', intervalParam);
      setQueryParams(params.toString());
      setCurrentPage(page);
    },
    []
  );

  // Handle interval button click - sets the time interval for data aggregation
  const handleIntervalClick = (label: string, intervalValue: string) => {
    setActiveButton(label);
    setInterval(intervalValue);

    // Trigger API call with new interval if site is selected
    if (selectedSiteId) {
      triggerQuery(selectedSiteId, dateTime.start_date, dateTime.end_date, 1, intervalValue);
    }
  };

  // Handle site change - trigger API call immediately
  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const newSiteId = value ? Number(value) : undefined;
    setSelectedSiteId(newSiteId);

    if (newSiteId) {
      triggerQuery(newSiteId, dateTime.start_date, dateTime.end_date, 1, interval);
    }
  };

  // Initialize datepickers
  useEffect(() => {
    let locale: AirDatepickerLocale = localeKo;
    if (i18n.language === 'eng') locale = localeEn;
    else if (i18n.language === 'ind') locale = localeId;

    const startDate = new Date(dateTime.start_date);
    const endDate = new Date(dateTime.end_date);

    const optionsStart = {
      autoClose: true,
      locale: locale,
      position: 'bottom center' as unknown as AirDatepickerPosition,
      selectedDates: [startDate],
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: (date: { formattedDate: string | string[] }) => {
        const newStartDate = Array.isArray(date.formattedDate)
          ? (date.formattedDate[0] ?? '')
          : date.formattedDate;
        setDateTime((prev) => ({
          ...prev,
          start_date: newStartDate,
        }));

        // Trigger API call immediately using refs for latest values (reset to page 1)
        if (selectedSiteIdRef.current) {
          const params = new URLSearchParams();
          params.append('site_id', selectedSiteIdRef.current.toString());
          params.append('start_time', getLocalISOString(newStartDate));
          params.append('end_time', getLocalISOString(dateTimeRef.current.end_date || ''));
          params.append('page', '1');
          params.append('size', PAGE_SIZE.toString());
          params.append('sort', 'desc');
          params.append('interval', intervalRef.current);
          setQueryParams(params.toString());
          setCurrentPage(1);
        }
      },
    };

    const optionsEnd = {
      autoClose: true,
      locale: locale,
      position: 'bottom center' as unknown as AirDatepickerPosition,
      selectedDates: [endDate],
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: (date: { formattedDate: string | string[] }) => {
        const newEndDate = Array.isArray(date.formattedDate)
          ? (date.formattedDate[0] ?? '')
          : date.formattedDate;
        setDateTime((prev) => ({
          ...prev,
          end_date: newEndDate,
        }));

        // Trigger API call immediately using refs for latest values (reset to page 1)
        if (selectedSiteIdRef.current) {
          const params = new URLSearchParams();
          params.append('site_id', selectedSiteIdRef.current.toString());
          params.append('start_time', getLocalISOString(dateTimeRef.current.start_date || ''));
          params.append('end_time', getLocalISOString(newEndDate));
          params.append('page', '1');
          params.append('size', PAGE_SIZE.toString());
          params.append('sort', 'desc');
          params.append('interval', intervalRef.current);
          setQueryParams(params.toString());
          setCurrentPage(1);
        }
      },
    };

    const datepicker1 = new AirDatepicker('[name="start_date"]', optionsStart);
    const datepicker2 = new AirDatepicker('[name="end_date"]', optionsEnd);

    return () => {
      datepicker1.destroy();
      datepicker2.destroy();
    };
  }, [i18n.language, dateTime.start_date, dateTime.end_date]);

  // Generate dynamic columns based on roads - always show 4 column sets
  const roadColumns: RoadColumn[] = useMemo(() => {
    const MIN_ROAD_COLUMNS = 4;
    const actualRoads = directionalTrafficData?.data?.roads || [];

    // Create columns for actual roads
    const columns: RoadColumn[] = actualRoads.map((road) => ({
      road_id: road.road_id,
      road_name: road.road_name || `방향 ${road.road_id}`,
      fields: {
        total: `road_${road.road_id}_total`,
        straight: `road_${road.road_id}_straight`,
        left: `road_${road.road_id}_left`,
        right: `road_${road.road_id}_right`,
        uturn: `road_${road.road_id}_uturn`,
      },
    }));

    // Add placeholder columns if fewer than MIN_ROAD_COLUMNS
    for (let i = columns.length; i < MIN_ROAD_COLUMNS; i++) {
      const placeholderId = `placeholder_${i + 1}`;
      columns.push({
        road_id: -i - 1, // Negative ID to indicate placeholder
        road_name: `방향 ${i + 1}`,
        fields: {
          total: `${placeholderId}_total`,
          straight: `${placeholderId}_straight`,
          left: `${placeholderId}_left`,
          right: `${placeholderId}_right`,
          uturn: `${placeholderId}_uturn`,
        },
      });
    }

    return columns;
  }, [directionalTrafficData]);

  // Common column settings
  const commonColumnSettings: Partial<ColumnDefinition> = {
    hozAlign: 'center',
    headerHozAlign: 'center',
    resizable: false,
    widthShrink: 0, // Prevent columns from shrinking below minWidth
  };

  // Build tabulator columns
  const columns: ColumnDefinition[] = useMemo(() => {
    const baseColumns: ColumnDefinition[] = [
      {
        title: '통계 수집 정보',
        headerHozAlign: 'center',
        columns: [
          {
            title: 'No',
            formatter: (cell: CellComponent) => {
              const row = cell.getRow();
              // desc 정렬: total_count부터 역순으로 표시
              const totalCount = directionalTrafficData?.data?.pagination?.total_count || 0;
              const rowPosition = row.getPosition(true);
              return totalCount - (currentPage - 1) * PAGE_SIZE - rowPosition + 1;
            },
            widthGrow: 1,
            minWidth: 50,
            headerSort: false,
            ...commonColumnSettings,
          },
          {
            title: '수집시각',
            field: 'formatted_time',
            widthGrow: 2,
            minWidth: 140,
            headerSort: false,
            ...commonColumnSettings,
            formatter: (cell: CellComponent<DirectionalTrafficRecord>) => {
              const data = cell.getData();
              // Use timestamp field directly (format: "YYYY-MM-DD HH:MM:SS")
              const timestamp = data.timestamp || '';
              if (timestamp) {
                // Return as "YYYY-MM-DD HH:MM" (remove seconds)
                return timestamp.slice(0, 16);
              }
              return '';
            },
          },
          {
            title: '교통량',
            field: 'total_traffic',
            widthGrow: 1,
            minWidth: 70,
            headerSort: false,
            ...commonColumnSettings,
          },
          {
            title: '직진',
            field: 'total_straight',
            widthGrow: 1,
            minWidth: 60,
            headerSort: false,
            ...commonColumnSettings,
          },
          {
            title: '좌회전',
            field: 'total_left',
            widthGrow: 1,
            minWidth: 60,
            headerSort: false,
            ...commonColumnSettings,
          },
          {
            title: '우회전',
            field: 'total_right',
            widthGrow: 1,
            minWidth: 60,
            headerSort: false,
            ...commonColumnSettings,
          },
          {
            title: '유턴',
            field: 'total_uturn',
            widthGrow: 1,
            minWidth: 60,
            headerSort: false,
            ...commonColumnSettings,
          },
        ],
      },
    ];

    // Add dynamic columns for each road
    roadColumns.forEach((road) => {
      baseColumns.push({
        title: road.road_name,
        headerHozAlign: 'center',
        cssClass: 'road-group-column',
        columns: [
          {
            title: '직진',
            field: road.fields.straight,
            widthGrow: 1,
            minWidth: 60,
            headerSort: false,
            cssClass: 'road-group-start',
            ...commonColumnSettings,
          },
          {
            title: '좌회전',
            field: road.fields.left,
            widthGrow: 1,
            minWidth: 60,
            headerSort: false,
            ...commonColumnSettings,
          },
          {
            title: '우회전',
            field: road.fields.right,
            widthGrow: 1,
            minWidth: 60,
            headerSort: false,
            ...commonColumnSettings,
          },
          {
            title: '유턴',
            field: road.fields.uturn,
            widthGrow: 1,
            minWidth: 60,
            headerSort: false,
            ...commonColumnSettings,
          },
        ],
      } as ColumnDefinition);
    });

    return baseColumns;
  }, [roadColumns, currentPage, directionalTrafficData]);

  // Tabulator options
  const languageTabulator = () => {
    const translationData = localStorage.getItem('translation_kor');
    const translations = translationData ? JSON.parse(translationData) : {};
    const t = (key: string) => translations[key] || key;

    return {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
  };

  const optionsTabulator: ReactTabulatorOptions = {
    pagination: false, // Disable local pagination - using server-side
    rowHeight: 41,
    movableRows: false,
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    layout: 'fitData', // Use fitData to maintain column widths and enable horizontal scroll
  };

  // Table data
  const tableData = useMemo(() => {
    if (!directionalTrafficData?.data?.data) return [];
    return directionalTrafficData.data.data.map((row, index) => ({
      ...row,
      id: index,
    }));
  }, [directionalTrafficData]);

  // Pagination info from API response
  const paginationInfo = useMemo(() => {
    return (
      directionalTrafficData?.data?.pagination || {
        page: 1,
        size: PAGE_SIZE,
        total_count: 0,
        total_pages: 0,
      }
    );
  }, [directionalTrafficData]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (selectedSiteId && newPage >= 1 && newPage <= paginationInfo.total_pages) {
      triggerQuery(selectedSiteId, dateTime.start_date, dateTime.end_date, newPage, interval);
    }
  };

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>통계</h3>
        <h3>&gt;</h3>
        <h3>환경 통계</h3>
      </div>

      <ContainerCard>
        <Filtering
          showLabel={false}
          disableFiltering={true}
          disableSearchInput={true}
          disableSearchButton={true}
          disableResetButton={true}
          customWidthSelect="w-full"
        >
          <div className="flex flex-col lg:flex-row gap-4 w-full items-start lg:items-center">
            {/* Left: Site dropdown */}
            <Select
              options={siteOptions}
              value={selectedSiteId !== undefined ? String(selectedSiteId) : undefined}
              onChange={handleSiteChange}
              customWidthSelect="w-full lg:w-[200px] flex-shrink-0"
            />

            {/* Right: Time filter buttons + Date picker */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center lg:ml-auto">
              {/* Time filter buttons */}
              <div className="flex w-fit gap-2 flex-shrink-0 items-center">
                <span className="text-sm text-gray-500 whitespace-nowrap">간격</span>
                <Button
                  customButton={`button filtering cursor-pointer hover:opacity-80 ${
                    activeButton === '1시간' ? 'active' : ''
                  }`}
                  label="1시간"
                  onClick={() => handleIntervalClick('1시간', '1h')}
                />
                <Button
                  customButton={`button filtering cursor-pointer hover:opacity-80 ${
                    activeButton === '12시간' ? 'active' : ''
                  }`}
                  label="12시간"
                  onClick={() => handleIntervalClick('12시간', '12h')}
                />
                <Button
                  customButton={`button filtering cursor-pointer hover:opacity-80 ${
                    activeButton === '1일' ? 'active' : ''
                  }`}
                  label="1일"
                  onClick={() => handleIntervalClick('1일', '1d')}
                />
              </div>

              {/* Date picker */}
              <div className="flex flex-row gap-2 items-center flex-shrink-0">
                <GeneralInput
                  isDob={true}
                  type="text"
                  name="start_date"
                  placeholder="YYYY-MM-DD hh:mm"
                  value={dateTime.start_date}
                  onChange={handleInputChange}
                  customInput="w-[170px]"
                />
                <span className="text-gray-500 flex-shrink-0">~</span>
                <GeneralInput
                  isDob={true}
                  type="text"
                  name="end_date"
                  placeholder="YYYY-MM-DD hh:mm"
                  value={dateTime.end_date}
                  onChange={handleInputChange}
                  customInput="w-[170px]"
                />
              </div>
            </div>
          </div>
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          {!queryParams || !queryParams.includes('site_id') ? (
            <div className="flex justify-center items-center p-8">
              <span className="text-gray-500">데이터를 불러오는 중입니다...</span>
            </div>
          ) : (
            <>
              {isLoading && (
                <div className="absolute inset-0 bg-white/70 dark:bg-[#0f1923]/70 flex justify-center items-center z-10">
                  <span className="dark:text-[#e0e0e0]">Loading...</span>
                </div>
              )}
              <div className="directional-traffic-wrapper">
                <ReactTabulator
                  data={tableData}
                  columns={columns}
                  options={optionsTabulator}
                  className="tabulator-custom directional-traffic-table"
                />
              </div>

              {/* Server-side Pagination Controls - Tabulator style */}
              <div className="tabulator-footer">
                <div className="tabulator-paginator">
                  <button
                    type="button"
                    className="tabulator-page"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    처음
                  </button>
                  <button
                    type="button"
                    className="tabulator-page"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    이전
                  </button>
                  <span className="tabulator-pages">
                    {Array.from({ length: Math.min(5, paginationInfo.total_pages) }, (_, i) => {
                      let pageNum: number;
                      if (paginationInfo.total_pages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= paginationInfo.total_pages - 2) {
                        pageNum = paginationInfo.total_pages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          type="button"
                          key={pageNum}
                          className={`tabulator-page ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </span>
                  <button
                    type="button"
                    className="tabulator-page"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === paginationInfo.total_pages}
                  >
                    다음
                  </button>
                  <button
                    type="button"
                    className="tabulator-page"
                    onClick={() => handlePageChange(paginationInfo.total_pages)}
                    disabled={currentPage === paginationInfo.total_pages}
                  >
                    끝
                  </button>
                </div>
                <div style={{ padding: '0 20px 0 0', textAlign: 'right' }}>
                  총 {paginationInfo.total_count} 건
                </div>
              </div>
            </>
          )}
        </div>
      </ContainerCard>
    </section>
  );
};

export default DirectionalTrafficStatisticPage;
